import { memo, useEffect, useState } from "react"
import { Trash2 } from "lucide-react"
import type { LeaseIntakeRowField, LeaseIntakeRowFields } from "@/app/api/leaseIntakes"
import { HoverTooltip } from "@/app/components/HoverTooltip"
import type { ClientTheme } from "@/app/lib/themes"
import { AMBER } from "@/app/pages/LeaseIntakePage/shared"

/** A staged row as held in the review draft. `key` is the server id, or a
 *  temporary `new-N` for rows added in this session (sent without an id). */
export interface DraftRow extends LeaseIntakeRowFields {
  key: string
  id?: string
  position: number
  include: boolean
  flags: string[]
  source_pages: string | null
  on_file: boolean
}

export const ROW_FIELDS: { field: LeaseIntakeRowField; label: string; width: number }[] = [
  { field: "lessor", label: "Lessor", width: 200 },
  { field: "lessee", label: "Lessee", width: 160 },
  { field: "lease_date", label: "Date", width: 100 },
  { field: "book", label: "Book", width: 64 },
  { field: "page", label: "Page", width: 64 },
  { field: "county", label: "County", width: 120 },
  { field: "str", label: "STR", width: 110 },
  { field: "legal", label: "Legal", width: 260 },
]

const DATA_FIELDS = new Set<LeaseIntakeRowField>(["lease_date", "book", "page", "str"])

const FLAG_HELP: Record<string, string> = {
  legal_unverified:
    "A second read of the document disagreed with the extracted legal description, so it was blanked. Check the document and enter it by hand.",
}

export function flagLabel(flag: string): string {
  const s = flag.replace(/_/g, " ")
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Holds its own text while focused and reports on blur/Enter, so typing
// re-renders one input rather than the whole (possibly 1,500-row) grid.
function CellInput({
  value,
  onCommit,
  width,
  mono,
  invalid,
  label,
  theme,
}: {
  value: string
  onCommit: (v: string) => void
  width: number
  mono: boolean
  invalid: boolean
  label: string
  theme: ClientTheme
}) {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  const commit = () => {
    if (text !== value) onCommit(text)
  }
  return (
    <input
      type="text"
      value={text}
      aria-label={label}
      title={text.length > 24 ? text : undefined}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit()
        if (e.key === "Escape") setText(value)
      }}
      style={{
        width: `${width}px`,
        padding: "4px 7px",
        borderRadius: "6px",
        backgroundColor: theme.bgSurface2,
        border: `1px solid ${invalid ? theme.brandAccent : theme.borderColor}`,
        color: theme.textPrimary,
        fontFamily: mono ? "var(--font-data)" : "var(--font-ui)",
        fontSize: "12px",
        outline: "none",
      }}
    />
  )
}

export function FlagChip({ flag, theme }: { flag: string; theme: ClientTheme }) {
  const emphasized = flag === "legal_unverified"
  const color = emphasized ? AMBER : theme.textMuted
  const chip = (
    <span
      tabIndex={FLAG_HELP[flag] ? 0 : undefined}
      style={{
        display: "inline-flex",
        fontFamily: "var(--font-ui)",
        fontSize: "10px",
        fontWeight: emphasized ? 700 : 500,
        padding: "1px 6px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        color,
        backgroundColor: emphasized ? `${AMBER}1A` : theme.bgSurface2,
        border: `1px solid ${emphasized ? `${AMBER}66` : theme.borderColor}`,
      }}
    >
      {flagLabel(flag)}
    </span>
  )
  return FLAG_HELP[flag] ? (
    <HoverTooltip content={FLAG_HELP[flag]} theme={theme}>
      {chip}
    </HoverTooltip>
  ) : (
    chip
  )
}

function OnFileStamp({ theme }: { theme: ClientTheme }) {
  return (
    <HoverTooltip
      content="A lease with this lessor, recording, and STR is already on the schedule. Committing merges into it (blank fields filled, differences reported as conflicts)."
      theme={theme}
    >
      <span
        tabIndex={0}
        style={{
          display: "inline-flex",
          fontFamily: "var(--font-ui)",
          fontSize: "10px",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          padding: "1px 6px",
          borderRadius: "4px",
          whiteSpace: "nowrap",
          color: theme.brandTeal,
          border: `1px solid ${theme.brandTeal}80`,
        }}
      >
        On file
      </span>
    </HoverTooltip>
  )
}

interface ReviewRowProps {
  row: DraftRow
  index: number
  onField: (key: string, field: LeaseIntakeRowField, value: string) => void
  onToggle: (key: string, include: boolean) => void
  onDelete: (key: string) => void
  theme: ClientTheme
}

// Memoized: the grid can hold ~1,500 rows, and callbacks are stable, so only
// the row whose object changed re-renders on an edit.
export const ReviewRow = memo(function ReviewRow({
  row,
  index,
  onField,
  onToggle,
  onDelete,
  theme,
}: ReviewRowProps) {
  const missingCounty = row.include && !(row.county ?? "").trim()
  return (
    <tr
      style={{
        borderBottom: `1px solid ${theme.borderColor}`,
        opacity: row.include ? 1 : 0.5,
      }}
    >
      <td className="pl-3 pr-1 py-1.5">
        <input
          type="checkbox"
          checked={row.include}
          onChange={(e) => onToggle(row.key, e.target.checked)}
          aria-label={`Include row ${index + 1}`}
          style={{ accentColor: theme.brandAccent }}
        />
      </td>
      <td
        className="px-1 py-1.5"
        style={{ fontFamily: "var(--font-data)", fontSize: "11px", color: theme.textDim }}
      >
        {index + 1}
      </td>
      {ROW_FIELDS.map(({ field, label, width }) => (
        <td key={field} className="px-1 py-1.5">
          <CellInput
            value={row[field] ?? ""}
            onCommit={(v) => onField(row.key, field, v)}
            width={width}
            mono={DATA_FIELDS.has(field)}
            invalid={field === "county" && missingCounty}
            label={`${label}, row ${index + 1}`}
            theme={theme}
          />
        </td>
      ))}
      <td className="px-1 py-1.5">
        <div className="flex flex-wrap items-center gap-1" style={{ minWidth: "90px" }}>
          {row.on_file && <OnFileStamp theme={theme} />}
          {row.flags.map((f) => (
            <FlagChip key={f} flag={f} theme={theme} />
          ))}
        </div>
      </td>
      <td
        className="px-1 py-1.5"
        style={{
          fontFamily: "var(--font-data)",
          fontSize: "11px",
          color: theme.textDim,
          whiteSpace: "nowrap",
        }}
      >
        {row.source_pages ?? "—"}
      </td>
      <td className="pl-1 pr-3 py-1.5 text-right">
        <button
          type="button"
          onClick={() => onDelete(row.key)}
          title="Delete row"
          aria-label={`Delete row ${index + 1}`}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: theme.textDim,
            padding: "4px",
            display: "inline-flex",
          }}
        >
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  )
})
